import { useState, useEffect, useCallback } from "react";

// ----------------------------------------------------------------------

type ReturnDateType = {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
};

export function useCountdownDate(targetDate?: string | null): ReturnDateType {
  const [countdown, setCountdown] = useState<ReturnDateType>({
    days: "00",
    hours: "00",
    minutes: "00",
    seconds: "00",
  });

  useEffect(() => {
    if (!targetDate) {
      setCountdown({
        days: "00",
        hours: "00",
        minutes: "00",
        seconds: "00",
      });
      return;
    }

    const targetTime = new Date(targetDate).getTime();

    if (Number.isNaN(targetTime)) {
      setCountdown({
        days: "00",
        hours: "00",
        minutes: "00",
        seconds: "00",
      });
      return;
    }

    const setNewTime = () => {
      const now = Date.now();
      const distanceToNow = targetTime - now;

      if (distanceToNow <= 0) {
        setCountdown({
          days: "00",
          hours: "00",
          minutes: "00",
          seconds: "00",
        });
        return;
      }

      const getDays = Math.floor(distanceToNow / (1000 * 60 * 60 * 24));
      const getHours = `0${Math.floor(
        (distanceToNow % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      )}`.slice(-2);
      const getMinutes = `0${Math.floor(
        (distanceToNow % (1000 * 60 * 60)) / (1000 * 60)
      )}`.slice(-2);
      const getSeconds = `0${Math.floor(
        (distanceToNow % (1000 * 60)) / 1000
      )}`.slice(-2);

      setCountdown({
        days: getDays.toString(),
        hours: getHours,
        minutes: getMinutes,
        seconds: getSeconds,
      });
    };

    setNewTime();
    const interval = setInterval(setNewTime, 1000);

    return () => clearInterval(interval);
  }, [targetDate]);

  return countdown;
}
// ----------------------------------------------------------------------

type ReturnSecondsType = {
  counting: boolean;
  countdown: number;
  startCountdown: VoidFunction;
  setCountdown: React.Dispatch<React.SetStateAction<number>>;
};

export function useCountdownSeconds(initCountdown: number): ReturnSecondsType {
  const [countdown, setCountdown] = useState(initCountdown);

  const startCountdown = useCallback(() => {
    let remainingSeconds = countdown;

    const intervalId = setInterval(() => {
      remainingSeconds -= 1;

      if (remainingSeconds === 0) {
        clearInterval(intervalId);
        setCountdown(initCountdown);
      } else {
        setCountdown(remainingSeconds);
      }
    }, 1000);
  }, [initCountdown, countdown]);

  const counting = initCountdown > countdown;

  return { counting, countdown, setCountdown, startCountdown };
}
